import { useEffect, useState, useCallback, useRef } from 'react';
import { useNotificationProvider } from 'hooks';
import { CANtrackComment } from 'containers/pages/comments/types/comment';
import getRecentComments from 'api/comments/getRecentComments';
import { convertToCANtrackComment } from 'containers/pages/comments/utils/convertToCANtrackComment';

const DEBOUNCE_MS = 300;
const ROWS_PER_PAGE = 50;

export interface CommentFilter {
	CustomerId: number | null;
	ConsultantId: number | null;
	Date: string;
}

const initFilter: CommentFilter = {
	CustomerId: null,
	ConsultantId: null,
	Date: '',
};

function useCommentsDashboard() {
	const { notifyError } = useNotificationProvider();

	const [isLoading, setIsLoading] = useState(false);
	const [comments, setComments] = useState<CANtrackComment[]>([]);
	const [filteredComments, setFilteredComments] = useState<CANtrackComment[]>([]);
	const [searchInput, setSearchInput] = useState('');
	const [filters, setFilters] = useState<CommentFilter>(initFilter);
	const [commentToCopy, setCommentToCopy] = useState<CANtrackComment>();
	const [page, setPage] = useState(0);
	const [totalResults, setTotalResults] = useState(0);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fetchComments = useCallback(async (currentFilters: CommentFilter, pageNum = 0) => {
		setIsLoading(true);
		try {
			const result = await getRecentComments({
				ConsultantId: currentFilters.ConsultantId?.toString() ?? undefined,
				CustomerId: currentFilters.CustomerId ?? undefined,
				Page: pageNum + 1,
				Take: ROWS_PER_PAGE,
			});
			const mapped = result.data.map(convertToCANtrackComment);
			setComments(mapped);
			setFilteredComments(mapped);
			setTotalResults(result.totalResults);
		} catch (error) {
			console.error('[useCommentsDashboard] Fetch failed:', error);
			notifyError('Failed to load comments');
		} finally {
			setIsLoading(false);
		}
	}, [notifyError]);

	// Initial load
	useEffect(() => {
		fetchComments(initFilter, 0);
	}, [fetchComments]);

	// Client-side search filtering
	const applySearch = useCallback((keyword: string, allComments: CANtrackComment[]) => {
		if (!keyword.trim()) {
			setFilteredComments(allComments);
			return;
		}
		const lower = keyword.toLowerCase();
		const filtered = allComments.filter(
			(c) =>
				c.customerName?.toLowerCase().includes(lower) ||
				c.caseTitle?.toLowerCase().includes(lower) ||
				c.title?.toLowerCase().includes(lower) ||
				c.description?.toLowerCase().includes(lower),
		);
		setFilteredComments(filtered);
	}, []);

	const onSearchInput = (input: string) => {
		setSearchInput(input);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			applySearch(input, comments);
		}, DEBOUNCE_MS);
	};

	const onResetSearch = () => {
		setSearchInput('');
		setFilteredComments(comments);
	};

	const onFilterChange = (field: keyof CommentFilter, value: unknown) => {
		setFilters((prev) => ({ ...prev, [field]: value }));
	};

	const onApplyFilters = () => {
		setPage(0);
		fetchComments(filters, 0);
	};

	const onClearFilters = () => {
		setFilters(initFilter);
		setSearchInput('');
		setPage(0);
		fetchComments(initFilter, 0);
	};

	const onPageChange = (_event: unknown, newPage: number) => {
		setPage(newPage);
		fetchComments(filters, newPage);
	};

	const handleCopyComment = (comment: CANtrackComment) => {
		setCommentToCopy({ ...comment, id: 0 });
	};

	const onRefresh = () => {
		fetchComments(filters, page);
	};

	return {
		state: {
			isLoading,
			comments: filteredComments,
			searchInput,
			filters,
			commentToCopy,
			page,
			totalResults,
			rowsPerPage: ROWS_PER_PAGE,
		},
		handler: {
			setComments,
			onSearchInput,
			onResetSearch,
			onFilterChange,
			onApplyFilters,
			onClearFilters,
			onPageChange,
			handleCopyComment,
			resetCommentToCopy: () => setCommentToCopy(undefined),
			onRefresh,
		},
	};
}

export default useCommentsDashboard;
